import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { itineraryApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fetchCityWikiImage } from '../components/LocationImage';
import {
  Search, MapPin, Calendar, Users, Wallet, Sparkles, ArrowRight,
  Globe, Clock, Loader2, X, Star, ChevronRight, Compass, Filter,
} from 'lucide-react';

const REGIONS = [
  { value: '', label: 'All Regions' },
  { value: 'Asia', label: 'Asia' },
  { value: 'Europe', label: 'Europe' },
  { value: 'Americas', label: 'Americas' },
  { value: 'Middle East', label: 'Middle East' },
  { value: 'Oceania', label: 'Oceania' },
];

const DURATIONS = [
  { value: '', label: 'Any Duration' },
  { value: 'short', label: '1-4 Days' },
  { value: 'medium', label: '5-7 Days' },
  { value: 'long', label: '8+ Days' },
];

const BUDGETS = [
  { value: '', label: 'Any Budget' },
  { value: 'low', label: 'Budget' },
  { value: 'medium', label: 'Moderate' },
  { value: 'flexible', label: 'Flexible' },
];

const TRAVELER_TYPES = [
  { value: '', label: 'Any Traveler' },
  { value: 'Solo', label: 'Solo' },
  { value: 'Couple', label: 'Couple' },
  { value: 'Family', label: 'Family' },
  { value: 'Friends', label: 'Friends' },
];

const BUDGET_COLORS = { low: 'bg-emerald-100 text-emerald-700', medium: 'bg-blue-100 text-blue-700', flexible: 'bg-purple-100 text-purple-700' };
const BUDGET_LABELS = { low: 'Budget', medium: 'Moderate', flexible: 'Flexible' };

const TemplateCard = ({ template, image, onUse, onPreview }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="group bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
    onClick={() => onPreview(template)}
    data-testid={`template-card-${template.id}`}
  >
    <div className="relative h-44 overflow-hidden">
      {image ? (
        <img src={image} alt={template.destination_city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-ocean to-sky flex items-center justify-center">
          <Globe className="w-12 h-12 text-white/40" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      <div className="absolute top-3 left-3 flex gap-1.5">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${BUDGET_COLORS[template.budget] || 'bg-gray-100 text-gray-700'}`}>
          {BUDGET_LABELS[template.budget] || template.budget}
        </span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-gray-700">
          {template.duration_days} Days
        </span>
      </div>
      {template.popularity > 0 && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-gold/90 text-ocean px-2 py-0.5 rounded-full text-[10px] font-bold">
          <Star className="w-3 h-3" /> {template.popularity} used
        </div>
      )}
      {template.source === 'community' && (
        <div className={`absolute ${template.popularity > 0 ? 'top-9' : 'top-3'} right-3 flex items-center gap-1 bg-white/90 text-ocean px-2 py-0.5 rounded-full text-[10px] font-bold`}>
          <Users className="w-3 h-3" /> Community
        </div>
      )}
      <div className="absolute bottom-3 left-3 right-3">
        <h3 className="font-serif text-lg text-white leading-tight drop-shadow-lg">{template.title}</h3>
      </div>
    </div>
    <div className="p-4 space-y-3">
      <p className="text-xs text-gray-500 line-clamp-2">{template.description}</p>
      <div className="flex flex-wrap gap-1.5">
        {template.highlights?.slice(0, 3).map(h => (
          <span key={h} className="text-[10px] px-2 py-0.5 bg-sky/10 text-ocean rounded-full">{h}</span>
        ))}
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-3 text-[11px] text-gray-400">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{template.destination_country}</span>
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{template.traveler_type}</span>
        </div>
        <Button
          size="sm"
          className="bg-gold hover:bg-gold-light text-ocean rounded-full text-xs px-3 h-7 font-semibold"
          onClick={(e) => { e.stopPropagation(); onUse(template); }}
          data-testid={`use-template-${template.id}`}
        >
          Use <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  </motion.div>
);

const TemplatePreview = ({ template, image, onUse, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    onClick={onClose}
    data-testid="template-preview-overlay"
  >
    <motion.div
      initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
      className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
      data-testid="template-preview-modal"
    >
      <div className="relative h-52 overflow-hidden rounded-t-2xl">
        {image ? (
          <img src={image} alt={template.destination_city} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-ocean to-sky" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50" data-testid="close-preview">
          <X className="w-4 h-4" />
        </button>
        <div className="absolute bottom-4 left-4 right-4">
          <h2 className="font-serif text-2xl text-white drop-shadow-lg">{template.title}</h2>
          <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
            <MapPin className="w-3.5 h-3.5" />{template.destination_city}, {template.destination_country}
            {template.source === 'community' && template.author_name && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-[10px]">by {template.author_name}</span>
            )}
          </p>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-600 leading-relaxed">{template.description}</p>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-ocean/5 rounded-xl p-3 text-center">
            <Clock className="w-4 h-4 text-ocean mx-auto mb-1" />
            <p className="text-xs text-gray-500">Duration</p>
            <p className="text-sm font-bold text-ocean">{template.duration_days} Days</p>
          </div>
          <div className="bg-ocean/5 rounded-xl p-3 text-center">
            <Wallet className="w-4 h-4 text-ocean mx-auto mb-1" />
            <p className="text-xs text-gray-500">Budget</p>
            <p className="text-sm font-bold text-ocean capitalize">{BUDGET_LABELS[template.budget] || template.budget}</p>
          </div>
          <div className="bg-ocean/5 rounded-xl p-3 text-center">
            <Users className="w-4 h-4 text-ocean mx-auto mb-1" />
            <p className="text-xs text-gray-500">Traveler</p>
            <p className="text-sm font-bold text-ocean">{template.traveler_type}</p>
          </div>
        </div>

        {template.highlights?.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Highlights</h4>
            <div className="flex flex-wrap gap-2">
              {template.highlights.map(h => (
                <span key={h} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-gold/10 text-ocean rounded-full border border-gold/20">
                  <Sparkles className="w-3 h-3 text-gold" /> {h}
                </span>
              ))}
            </div>
          </div>
        )}

        {template.travel_styles?.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Travel Style</h4>
            <div className="flex flex-wrap gap-2">
              {template.travel_styles.map(s => (
                <span key={s} className="text-xs px-3 py-1 bg-sky/10 text-ocean rounded-full capitalize">{s}</span>
              ))}
            </div>
          </div>
        )}

        <Button
          className="w-full bg-gold hover:bg-gold-light text-ocean rounded-full font-semibold py-3 shadow-lg hover:shadow-xl"
          onClick={() => onUse(template)}
          data-testid="use-template-preview-btn"
        >
          <Compass className="w-4 h-4 mr-2" /> Customize & Create Trip
        </Button>
      </div>
    </motion.div>
  </motion.div>
);

const FilterChip = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
      active ? 'bg-ocean text-white shadow-md' : 'bg-white/80 text-gray-600 hover:bg-ocean/10 border border-gray-200'
    }`}
  >
    {label}
  </button>
);

export default function TemplatesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [region, setRegion] = useState('');
  const [duration, setDuration] = useState('');
  const [budget, setBudget] = useState('');
  const [travelerType, setTravelerType] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [images, setImages] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const fetchedCities = useRef(new Set());

  const fetchImages = useCallback((templates) => {
    const cities = [...new Set(templates.map(t => t.destination_city))];
    cities.forEach(async (city) => {
      if (!fetchedCities.current.has(city)) {
        fetchedCities.current.add(city);
        const url = await fetchCityWikiImage(city);
        if (url) setImages(prev => ({ ...prev, [city]: url }));
      }
    });
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (region) params.region = region;
      if (duration) params.duration = duration;
      if (budget) params.budget = budget;
      if (travelerType) params.traveler_type = travelerType;
      if (searchQuery) params.search = searchQuery;
      const res = await itineraryApi.getTemplates(params);
      if (res.data.success) {
        setTemplates(res.data.templates);
        fetchImages(res.data.templates);
      }
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [region, duration, budget, travelerType, searchQuery, fetchImages]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTemplates();
  };

  const handleUseTemplate = async (template) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to use templates');
      navigate('/auth');
      return;
    }
    try {
      const res = await itineraryApi.useTemplate(template.id);
      if (res.data.success) {
        sessionStorage.setItem('templateTrip', JSON.stringify(res.data.trip_input));
        toast.success(`Template "${template.title}" selected! Customize your dates and preferences.`);
        navigate('/new-trip?template=true');
      }
    } catch { toast.error('Failed to use template'); }
  };

  const clearFilters = () => {
    setRegion(''); setDuration(''); setBudget(''); setTravelerType(''); setSearchQuery('');
  };

  const hasFilters = region || duration || budget || travelerType || searchQuery;

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="templates-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/80 to-sky/60" />
        <div className="absolute top-40 left-10 w-80 h-80 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
      </div>

      <Header />

      {/* Hero */}
      <div className="pt-20 pb-10 px-6 md:px-12 lg:px-24 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-3 drop-shadow-lg" data-testid="templates-title">
            Trip Templates
          </h1>
          <p className="text-white/70 text-base max-w-xl mx-auto mb-8">
            Browse curated itinerary templates. Pick one, customize your dates, and go.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="max-w-md mx-auto flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search destinations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white/95 backdrop-blur-xl border-white/30 rounded-full"
                data-testid="template-search-input"
              />
            </div>
            <Button type="submit" className="bg-gold hover:bg-gold-light text-ocean rounded-full px-5" data-testid="template-search-btn">
              Search
            </Button>
          </form>

          {/* Filter Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="text-white/70 hover:text-white hover:bg-white/10 text-xs"
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-3.5 h-3.5 mr-1.5" /> {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </motion.div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="max-w-2xl mx-auto mt-4 bg-white/10 backdrop-blur-xl rounded-xl p-4 border border-white/20 space-y-3" data-testid="filter-panel">
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider mb-1.5 text-left">Region</p>
                  <div className="flex flex-wrap gap-1.5">
                    {REGIONS.map(r => <FilterChip key={r.value} label={r.label} active={region === r.value} onClick={() => setRegion(r.value)} />)}
                  </div>
                </div>
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider mb-1.5 text-left">Duration</p>
                  <div className="flex flex-wrap gap-1.5">
                    {DURATIONS.map(d => <FilterChip key={d.value} label={d.label} active={duration === d.value} onClick={() => setDuration(d.value)} />)}
                  </div>
                </div>
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider mb-1.5 text-left">Budget</p>
                  <div className="flex flex-wrap gap-1.5">
                    {BUDGETS.map(b => <FilterChip key={b.value} label={b.label} active={budget === b.value} onClick={() => setBudget(b.value)} />)}
                  </div>
                </div>
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider mb-1.5 text-left">Traveler Type</p>
                  <div className="flex flex-wrap gap-1.5">
                    {TRAVELER_TYPES.map(t => <FilterChip key={t.value} label={t.label} active={travelerType === t.value} onClick={() => setTravelerType(t.value)} />)}
                  </div>
                </div>
                {hasFilters && (
                  <div className="text-right">
                    <button onClick={clearFilters} className="text-white/60 hover:text-white text-xs underline">Clear all filters</button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Templates Grid */}
      <main className="px-6 md:px-12 lg:px-24 pb-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-white/60" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-20">
              <Globe className="w-12 h-12 text-white/30 mx-auto mb-4" />
              <p className="text-white/60 text-lg">No templates match your filters.</p>
              <Button variant="ghost" onClick={clearFilters} className="text-gold hover:text-gold-light mt-3">
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <p className="text-white/50 text-xs mb-4">{templates.length} template{templates.length !== 1 ? 's' : ''} found</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="templates-grid">
                {templates.map((t, i) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    image={images[t.destination_city]}
                    onUse={handleUseTemplate}
                    onPreview={setPreviewTemplate}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewTemplate && (
          <TemplatePreview
            template={previewTemplate}
            image={images[previewTemplate.destination_city]}
            onUse={handleUseTemplate}
            onClose={() => setPreviewTemplate(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
